import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useHistory } from 'react-router-dom';
import { pathgen } from 'object-path-generator';
import { DropdownMenu, TableList, TableListActions, Typography, useDevice } from 'seagull';
import { Archive, CalendarRemove, Copy, Edit, EmailSent, Eye, Trash } from 'seagull/icons';
import { VariableInlineRenderer } from '~/components/AgreementStudioPage/TemplateVariables/VariableInlineRenderer/VariableInlineRenderer';
import { routes } from '~/constants';
import { useStore, useStoreActions } from '~/contexts';
import { useContactsMap } from '~/hooks/reactQuery/derived/useContactsMap';
import { useArchiveDraft } from '~/hooks/reactQuery/mutations/useArchiveDraft';
import { useDuplicateDraft } from '~/hooks/reactQuery/mutations/useDuplicateDraft';
import { usePreviewDraft } from '~/hooks/usePreviewDraft';
import { useScheduledProposal } from '~/hooks/useScheduledProposal';
import { useTerminateAgreement } from '~/hooks/useTerminateAgreement';
import { useLocaleKeys } from '~/i18n';
import { DraftStatusType, timeModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import styles from './DraftsDashboard.module.scss';
import { getUICompanyName } from './DraftsDashboard.utils';
import { ScheduledTooltip } from './ScheduledTooltip';
export const DraftsDashboardRowTestIds = pathgen('DraftsDashboardRowTestIds');
export default function DraftsDashboardRow(props) {
    var _draft_lastModifiedBy, _draft_lastModifiedBy1;
    const { draft, index, refresh } = props;
    const { t: rowTexts } = useLocaleKeys('pages.draftsDashboard.row');
    const { t: draftsTexts } = useLocaleKeys('pages.draftsDashboard');
    const { t: editTableTexts } = useLocaleKeys('pages.draftsDashboard.editTable');
    const { logBI } = useStoreActions();
    const { isMobileDevice } = useDevice();
    const history = useHistory();
    const { terminateDraftById } = useTerminateAgreement();
    const worksAt = useStore(selectors.user.business);
    const { contactsMap, sampleContact } = useContactsMap(worksAt === null || worksAt === void 0 ? void 0 : worksAt.id);
    const { archive } = useArchiveDraft({
        onSuccess: ()=>refresh()
    });
    const { duplicateDraft } = useDuplicateDraft({
        onSuccess: (newDraftId)=>history.push(routes.draft(newDraftId))
    });
    const companyName = getUICompanyName(draft, editTableTexts.anchorSample());
    const title = draft.title;
    const isScheduledProposal = draft.status.type === DraftStatusType.Scheduled;
    const { previewDraft } = usePreviewDraft({
        sampleContact,
        contactsMap
    });
    const { sendNow, cancelScheduledProposal, showSwitchToEditModal } = useScheduledProposal({
        draftId: draft.id,
        refresh,
        isSampleRelationship: draft.isSample
    });
    const editDraft = ()=>{
        if (isScheduledProposal) {
            showSwitchToEditModal();
        } else {
            logBI(10352);
            history.push(routes.draft(draft.id));
        }
    };
    const handlePreviewDraft = ()=>{
        previewDraft(draft.id);
    };
    function renderMobileCompanyNameCell() {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                companyName && /*#__PURE__*/ _jsx(Typography, {
                    ellipsis: true,
                    weight: "normal",
                    testId: "draft-company-name-".concat(index),
                    children: companyName
                }),
                title && /*#__PURE__*/ _jsx(Typography, {
                    ellipsis: true,
                    skin: "secondary",
                    testId: "draft-name-".concat(index),
                    style: {
                        paddingBlock: 4
                    },
                    children: /*#__PURE__*/ _jsx(VariableInlineRenderer, {
                        text: title,
                        mode: "text"
                    })
                })
            ]
        });
    }
    function renderDesktopCompanyNameCell() {
        return companyName ? /*#__PURE__*/ _jsxs("div", {
            className: styles.scheduledTooltip,
            children: [
                /*#__PURE__*/ _jsxs(Typography, {
                    ellipsis: true,
                    weight: "normal",
                    testId: "draft-company-name-".concat(index),
                    children: [
                        companyName,
                        title && /*#__PURE__*/ _jsxs(Typography, {
                            as: "span",
                            variant: "paragraph2",
                            skin: "secondary",
                            testId: "draft-name-".concat(index),
                            children: [
                                ' ',
                                "(",
                                /*#__PURE__*/ _jsx(VariableInlineRenderer, {
                                    text: title,
                                    mode: "text"
                                }),
                                ")"
                            ]
                        })
                    ]
                }),
                draft.status.type === DraftStatusType.Scheduled && /*#__PURE__*/ _jsx(ScheduledTooltip, {
                    index: index,
                    scheduleDate: draft.status.schedule.time
                })
            ]
        }) : /*#__PURE__*/ _jsxs(Typography, {
            as: "span",
            variant: "paragraph2",
            skin: "secondary",
            testId: "draft-name-".concat(index),
            children: [
                "(",
                /*#__PURE__*/ _jsx(VariableInlineRenderer, {
                    text: title || rowTexts.unnamedDraft(),
                    mode: "text"
                }),
                ")"
            ]
        });
    }
    return /*#__PURE__*/ _jsx(TableList.Row, {
        rowId: draft.id,
        testId: "drafts-row",
        "data-heapid": "draft",
        as: 'button',
        to: routes.draft(draft.id),
        onClick: ()=>{
            logBI(10352);
            history.push(routes.draft(draft.id));
        },
        items: [
            {
                value: isMobileDevice ? renderMobileCompanyNameCell() : renderDesktopCompanyNameCell()
            },
            {
                value: /*#__PURE__*/ _jsx(Typography, {
                    children: "".concat((_draft_lastModifiedBy = draft.lastModifiedBy) === null || _draft_lastModifiedBy === void 0 ? void 0 : _draft_lastModifiedBy.firstName, " ").concat((_draft_lastModifiedBy1 = draft.lastModifiedBy) === null || _draft_lastModifiedBy1 === void 0 ? void 0 : _draft_lastModifiedBy1.lastName)
                })
            },
            {
                value: /*#__PURE__*/ _jsx(Typography, {
                    skin: "secondary",
                    children: timeModel.formatTimeAgo(draft.lastModifiedOn)
                })
            }
        ],
        actionColumn: /*#__PURE__*/ _jsxs(TableListActions, {
            buttonText: rowTexts.edit(),
            buttonIcon: /*#__PURE__*/ _jsx(Edit, {}),
            testId: "table-list-actions-".concat(index),
            btnHeapId: "draft-view-btn",
            menuHeapId: "draft-menu-btn",
            onClickButton: editDraft,
            mobile: {
                title: companyName,
                subtitle: title
            },
            children: [
                /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    onClick: handlePreviewDraft,
                    iconStart: /*#__PURE__*/ _jsx(Eye, {}),
                    testId: "table-list-actions-preview-".concat(index),
                    children: rowTexts.preview()
                }),
                isScheduledProposal && /*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                            onClick: sendNow,
                            iconStart: /*#__PURE__*/ _jsx(EmailSent, {}),
                            testId: "table-list-actions-send-now-".concat(index),
                            children: draftsTexts.sendNow()
                        }),
                        /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                            onClick: cancelScheduledProposal,
                            iconStart: /*#__PURE__*/ _jsx(CalendarRemove, {
                                size: 18
                            }),
                            testId: "table-list-actions-cancel-".concat(index),
                            children: draftsTexts.cancelScheduling()
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    onClick: ()=>{
                        duplicateDraft(draft.id);
                    },
                    iconStart: /*#__PURE__*/ _jsx(Copy, {}),
                    testId: "table-list-actions-duplicate-".concat(index),
                    "data-heapid": "draft-menu-duplicate",
                    children: rowTexts.duplicate()
                }),
                /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    onClick: ()=>{
                        logBI(18800);
                        archive(draft.id);
                    },
                    iconStart: /*#__PURE__*/ _jsx(Archive, {}),
                    disabled: (draft === null || draft === void 0 ? void 0 : draft.status.type) === DraftStatusType.Scheduled,
                    tooltipProps: {
                        title: (draft === null || draft === void 0 ? void 0 : draft.status.type) === DraftStatusType.Scheduled ? rowTexts.cancelSchedulingBeforeArchiving() : undefined
                    },
                    testId: DraftsDashboardRowTestIds.MoreActions.Archive(index),
                    children: rowTexts.archive()
                }),
                /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    onClick: ()=>terminateDraftById(draft.id, ()=>refresh()),
                    skin: "error",
                    iconStart: /*#__PURE__*/ _jsx(Trash, {}),
                    testId: "table-list-actions-discard-".concat(index),
                    "data-heapid": "draft-menu-discard",
                    children: rowTexts.delete()
                })
            ]
        })
    }, draft.id);
}
